/**
 * PurchaseInvoicesListController — page /factures-recues.html (EPIC-017 / 17.5).
 *
 * Liste paginée des factures reçues de l'org. Le `connect()` déclenche
 * `GET /purchase-invoices` qui, côté backend, lance le `syncFromGateway`
 * (polling PA SuperPDP) avant de retourner la liste paginée.
 *
 * Filtres : statut (validation/lifecycle dispatch), origine (pdp/upload),
 * période (raccourcis présets convertis en bornes `YYYY-MM,YYYY-MM`),
 * fournisseur (SIRET strict — input numérique ≥9 chars).
 *
 * Tri V1 : `issuedAt` et `amountInclVat` triables côté backend ;
 * `supplierName` et `validationStatus` listés dans `ALLOWED_SORT_FIELDS`
 * mais sans mapping différentiel — indicateur visuel affiché, tri retombe
 * sur le défaut `issued_at_desc`.
 *
 * Modal upload : `<dialog>` natif HTML5. Drag & drop + click sur
 * « Parcourir » → validation client MIME `application/pdf` + taille ≤10 Mo
 * → `POST /purchase-invoices/upload` (multipart, header
 * `Idempotency-Key` UUID v4). Succès → redirect `/factures-recues-detail.html?id=…`.
 *
 * Export CSV : génération côté navigateur à partir du dernier lot reçu
 * (`_lastResults`), encodage UTF-8 BOM + séparateur `;` (Excel FR).
 *
 * Conventions verbatim : aucun HTML inline en JS — `<template>` +
 * `cloneTemplate()` + `fillSlots()` exclusivement.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const HISTORY_STORAGE_KEY = "nm_filter_origin_factures_recues";

const ORIGIN_LABELS = {
  pdp: "PA",
  upload: "Upload",
};

const STATUS_LABELS = {
  pending: "à valider",
  validated: "validée",
  paid: "payée",
  refused: "rejetée",
  aborted: "annulée",
  received: "reçue",
  created: "créée",
  checked: "vérifiée",
};

const STATUS_PILL_MODIFIER = {
  pending: "nm-pill--ochre",
  validated: "nm-pill--blue",
  paid: "nm-pill--olive",
  refused: "nm-pill--red",
  aborted: "",
};

const STATUS_SELECT_DISPATCH = {
  pending: { field: "validationStatus", value: "pending" },
  validated: { field: "validationStatus", value: "validated" },
  paid: { field: "status", value: "paid" },
  rejected: { field: "status", value: "refused" },
};

const SORTABLE_KEYS = new Set([
  "issuedAt",
  "supplierName",
  "amountInclVat",
  "validationStatus",
]);

export default class PurchaseInvoicesListController extends BaseController {
  static targets = [
    "statusFilter",
    "originFilter",
    "periodFilter",
    "supplierFilter",
    "historyToggleGroup",
    "historyToggle",
    "summary",
    "tableWrap",
    "tableBody",
    "loadingRow",
    "pagination",
    "pageInfo",
    "pageButtons",
    "uploadModal",
    "uploadForm",
    "dropzone",
    "fileInput",
    "uploadProgress",
    "uploadFileName",
    "uploadError",
    "uploadSubmit",
  ];

  static values = {
    page: { type: Number, default: 1 },
    perPage: { type: Number, default: 25 },
    sort: { type: String, default: "-issuedAt" },
    historyFilter: { type: String, default: "all" },
  };

  connect() {
    this._debounceTimer = null;
    this._fetchSeq = 0;
    this._lastResults = [];
    this._pendingFile = null;
    this._uploading = false;
    this._restoreFromQueryString();
    this._restoreHistoryFromSession();
    this._loadImportCutover();
    this.refresh();
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  // ---------------------------------------------------------------------------
  // Querystring persistence
  // ---------------------------------------------------------------------------

  _restoreFromQueryString() {
    const params = new URLSearchParams(window.location.search);
    const page = Number(params.get("page"));
    if (Number.isFinite(page) && page >= 1) this.pageValue = page;
    if (params.has("sort")) this.sortValue = params.get("sort") || "-issuedAt";

    const apply = (key, target) => {
      const v = params.get(`filter[${key}]`);
      if (v !== null && target) target.value = v;
    };
    if (this.hasStatusFilterTarget) apply("statusUi", this.statusFilterTarget);
    if (this.hasOriginFilterTarget) apply("origin", this.originFilterTarget);
    if (this.hasPeriodFilterTarget) apply("periodUi", this.periodFilterTarget);
    if (this.hasSupplierFilterTarget) apply("supplierSiret", this.supplierFilterTarget);
  }

  _syncQueryString() {
    const params = new URLSearchParams();
    if (this.pageValue > 1) params.set("page", String(this.pageValue));
    if (this.sortValue && this.sortValue !== "-issuedAt") params.set("sort", this.sortValue);
    if (this.hasStatusFilterTarget && this.statusFilterTarget.value) {
      params.set("filter[statusUi]", this.statusFilterTarget.value);
    }
    if (this.hasOriginFilterTarget && this.originFilterTarget.value) {
      params.set("filter[origin]", this.originFilterTarget.value);
    }
    if (this.hasPeriodFilterTarget && this.periodFilterTarget.value) {
      params.set("filter[periodUi]", this.periodFilterTarget.value);
    }
    if (this.hasSupplierFilterTarget && this.supplierFilterTarget.value) {
      params.set("filter[supplierSiret]", this.supplierFilterTarget.value);
    }
    const qs = params.toString();
    const newUrl = window.location.pathname + (qs ? `?${qs}` : "");
    window.history.replaceState(null, "", newUrl);
  }

  // ---------------------------------------------------------------------------
  // Filter callbacks
  // ---------------------------------------------------------------------------

  applyFilter() {
    this.pageValue = 1;
    this.refresh();
  }

  applyFilterDebounced() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => {
      this.pageValue = 1;
      this.refresh();
    }, 300);
  }

  resetFilters() {
    if (this.hasStatusFilterTarget) this.statusFilterTarget.value = "";
    if (this.hasOriginFilterTarget) this.originFilterTarget.value = "";
    if (this.hasPeriodFilterTarget) this.periodFilterTarget.value = "y_current";
    if (this.hasSupplierFilterTarget) this.supplierFilterTarget.value = "";
    this.historyFilterValue = "all";
    if (this.hasHistoryToggleTarget) this.historyToggleTarget.checked = true;
    this._persistHistoryToSession();
    this.pageValue = 1;
    this.sortValue = "-issuedAt";
    this.refresh();
  }

  changeHistory(event) {
    this.historyFilterValue = event.target.checked ? "all" : "native";
    this._persistHistoryToSession();
    this.pageValue = 1;
    this.refresh();
  }

  _restoreHistoryFromSession() {
    try {
      const stored = window.sessionStorage.getItem(HISTORY_STORAGE_KEY);
      if (stored === "native" || stored === "all") {
        this.historyFilterValue = stored;
        if (this.hasHistoryToggleTarget) this.historyToggleTarget.checked = stored === "all";
      }
    } catch {
      // sessionStorage indisponible — fallback "all".
    }
  }

  _persistHistoryToSession() {
    try {
      window.sessionStorage.setItem(HISTORY_STORAGE_KEY, this.historyFilterValue);
    } catch {
      // sessionStorage indisponible — fail silencieux.
    }
  }

  async _loadImportCutover() {
    if (!this.hasHistoryToggleGroupTarget) return;
    try {
      const profile = await api.getOrganizationProfile();
      const cutover = profile?.importCutoverDate ?? null;
      this.historyToggleGroupTarget.hidden = !cutover;
    } catch {
      this.historyToggleGroupTarget.hidden = true;
    }
  }

  sort(event) {
    const btn = event.currentTarget;
    const key = btn.dataset.sortKey;
    if (!SORTABLE_KEYS.has(key)) return;
    const current = this.sortValue;
    if (current === key) {
      this.sortValue = `-${key}`;
    } else if (current === `-${key}`) {
      this.sortValue = key;
    } else {
      this.sortValue = `-${key}`;
    }
    this.pageValue = 1;
    this.refresh();
  }

  paginate(event) {
    const page = Number(event.currentTarget.dataset.page);
    if (!Number.isFinite(page) || page < 1) return;
    this.pageValue = page;
    this.refresh();
  }

  // ---------------------------------------------------------------------------
  // Period preset → YYYY-MM,YYYY-MM
  // ---------------------------------------------------------------------------

  _periodPresetToRange(preset) {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const fmt = (yyyy, mm) => `${yyyy}-${String(mm + 1).padStart(2, "0")}`;
    if (preset === "this_month") return `${fmt(y, m)},${fmt(y, m)}`;
    if (preset === "last_month") {
      const d = new Date(y, m - 1, 1);
      return `${fmt(d.getFullYear(), d.getMonth())},${fmt(d.getFullYear(), d.getMonth())}`;
    }
    if (preset === "q_current") {
      const qStartMonth = Math.floor(m / 3) * 3;
      return `${fmt(y, qStartMonth)},${fmt(y, qStartMonth + 2)}`;
    }
    if (preset === "y_current") {
      return `${fmt(y, 0)},${fmt(y, 11)}`;
    }
    if (preset === "last_year") {
      return `${fmt(y - 1, 0)},${fmt(y - 1, 11)}`;
    }
    if (preset === "last_12m") {
      const start = new Date(y, m - 11, 1);
      return `${fmt(start.getFullYear(), start.getMonth())},${fmt(y, m)}`;
    }
    return "";
  }

  // ---------------------------------------------------------------------------
  // Fetch + render
  // ---------------------------------------------------------------------------

  async refresh() {
    const seq = ++this._fetchSeq;
    if (this.hasTableWrapTarget) this.tableWrapTarget.setAttribute("aria-busy", "true");
    this._syncQueryString();
    this._updateSortIndicators();

    const params = { page: this.pageValue, perPage: this.perPageValue };
    if (this.sortValue) params.sort = this.sortValue;

    if (this.hasStatusFilterTarget && this.statusFilterTarget.value) {
      const dispatch = STATUS_SELECT_DISPATCH[this.statusFilterTarget.value];
      if (dispatch) params[`filter[${dispatch.field}]`] = dispatch.value;
    }
    if (this.hasOriginFilterTarget && this.originFilterTarget.value) {
      params["filter[origin]"] = this.originFilterTarget.value;
    }
    if (this.hasPeriodFilterTarget && this.periodFilterTarget.value) {
      const range = this._periodPresetToRange(this.periodFilterTarget.value);
      if (range) params["filter[period]"] = range;
    }
    if (this.hasSupplierFilterTarget) {
      const raw = (this.supplierFilterTarget.value || "").replace(/\s+/g, "");
      if (/^\d{9,14}$/.test(raw)) {
        params["filter[supplierSiret]"] = raw;
      }
    }
    if (this.historyFilterValue && this.historyFilterValue !== "all") {
      params.origin = this.historyFilterValue;
    }

    try {
      const response = await api.listPurchaseInvoices(params);
      if (seq !== this._fetchSeq) return;
      const items = Array.isArray(response?.data) ? response.data : [];
      const pagination = response?.pagination ?? {
        page: 1, perPage: this.perPageValue, total: 0, totalPages: 1,
      };
      this._lastResults = items;
      this._renderRows(items);
      this._renderPagination(pagination);
      this._renderSummary(response?.summary ?? null);
    } catch (error) {
      if (seq !== this._fetchSeq) return;
      this._renderError();
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger les factures reçues.";
      this.showError(msg);
    } finally {
      if (seq === this._fetchSeq && this.hasTableWrapTarget) {
        this.tableWrapTarget.setAttribute("aria-busy", "false");
      }
    }
  }

  _renderRows(items) {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();

    if (items.length === 0) {
      this.tableBodyTarget.appendChild(this.cloneTemplate("tpl-purchase-empty"));
      return;
    }

    for (const inv of items) {
      const fragment = this.cloneTemplate("tpl-purchase-row");
      const detailUrl = `/factures-recues-detail.html?id=${encodeURIComponent(inv.id)}`;
      const pillKind = this._resolvePillKind(inv);

      this.fillSlots(fragment, {
        supplierName: inv.supplierName ?? "—",
        originLabel: ORIGIN_LABELS[inv.origin] ?? "",
        issuedAt: inv.issuedAt ? this.formatDate(inv.issuedAt) : "—",
        dueAt: inv.dueAt ? this.formatDate(inv.dueAt) : "—",
        amountExclVat: this._formatAmount(inv.amountExclVat),
        vatTotal: this._formatAmount(inv.vatTotal),
        amountInclVat: this._formatAmount(inv.amountInclVat, true),
        statusLabel: STATUS_LABELS[pillKind] ?? pillKind,
        actionLabel: `Voir la facture ${inv.pdpGatewayInvoiceId ?? inv.supplierName ?? inv.id}`,
      });

      const supplierLink = fragment.querySelector('[data-slot-attr-href="detailUrl"]');
      if (supplierLink) supplierLink.setAttribute("href", detailUrl);
      const actionLink = fragment.querySelector('[data-slot-attr-href="actionUrl"]');
      if (actionLink) actionLink.setAttribute("href", detailUrl);

      const issuedEl = fragment.querySelector('[data-slot-attr-datetime="issuedAtIso"]');
      if (issuedEl && inv.issuedAt) issuedEl.setAttribute("datetime", inv.issuedAt);
      const dueEl = fragment.querySelector('[data-slot-attr-datetime="dueAtIso"]');
      if (dueEl && inv.dueAt) dueEl.setAttribute("datetime", inv.dueAt);

      const pill = fragment.querySelector('[data-slot-element="statusPill"]');
      const modifier = STATUS_PILL_MODIFIER[pillKind];
      if (pill && modifier) pill.classList.add(modifier);

      const importedBadge = fragment.querySelector('[data-slot-element="importedBadge"]');
      if (importedBadge && inv.origin === "imported") importedBadge.hidden = false;

      this.tableBodyTarget.appendChild(fragment);
    }
  }

  _resolvePillKind(inv) {
    if (inv.status === "paid") return "paid";
    if (inv.status === "refused" || inv.status === "aborted") return inv.status;
    if (inv.validationStatus === "pending") return "pending";
    if (inv.validationStatus === "validated") return "validated";
    return inv.status ?? "pending";
  }

  _renderPagination(pagination) {
    if (!this.hasPaginationTarget || !this.hasPageButtonsTarget || !this.hasPageInfoTarget) {
      return;
    }
    const page = Number(pagination.page) || 1;
    const perPage = Number(pagination.perPage) || this.perPageValue;
    const total = Number(pagination.total) || 0;
    const totalPages = Number(pagination.totalPages) || 1;

    if (total === 0 || totalPages <= 1) {
      this.paginationTarget.hidden = true;
      this.pageInfoTarget.textContent = total === 0
        ? "Aucune facture reçue"
        : `${total} facture${total > 1 ? "s" : ""}`;
      return;
    }
    this.paginationTarget.hidden = false;

    const start = (page - 1) * perPage + 1;
    const end = Math.min(page * perPage, total);
    this.pageInfoTarget.textContent = `${start} — ${end} sur ${total} factures`;

    while (this.pageButtonsTarget.firstChild) {
      this.pageButtonsTarget.removeChild(this.pageButtonsTarget.firstChild);
    }
    this.pageButtonsTarget.appendChild(this._buildPageButton({
      page: page - 1, text: "‹", label: "Page précédente", disabled: page <= 1,
    }));
    for (const token of this.getPagesToShow(page, totalPages)) {
      if (token === "...") {
        this.pageButtonsTarget.appendChild(this.cloneTemplate("tpl-pagination-ellipsis"));
        continue;
      }
      this.pageButtonsTarget.appendChild(this._buildPageButton({
        page: token, text: String(token), label: `Page ${token}`, current: token === page,
      }));
    }
    this.pageButtonsTarget.appendChild(this._buildPageButton({
      page: page + 1, text: "›", label: "Page suivante", disabled: page >= totalPages,
    }));
  }

  _buildPageButton({ page, text, label, current = false, disabled = false }) {
    const fragment = this.cloneTemplate("tpl-pagination-page");
    const btn = fragment.querySelector("button");
    if (!btn) return fragment;
    btn.textContent = text;
    btn.setAttribute("aria-label", label);
    btn.dataset.page = String(page);
    if (disabled) btn.disabled = true;
    if (current) {
      btn.classList.add("is-current");
      btn.setAttribute("aria-current", "page");
    }
    return fragment;
  }

  // Sous-titre d'en-tête (§8, 99.83) — agrégats globaux de l'organization
  // renvoyés dans `response.summary` (totalCount, pendingCount, ttcLast12Months).
  _renderSummary(summary) {
    if (!this.hasSummaryTarget) return;
    if (!summary) {
      this.summaryTarget.textContent = "—";
      return;
    }
    const total = Number(summary.totalCount) || 0;
    if (total === 0) {
      this.summaryTarget.textContent = "Aucune facture fournisseur pour l'instant.";
      return;
    }
    const pending = Number(summary.pendingCount) || 0;
    const ttc = Number(summary.ttcLast12Months) || 0;
    const factures = total > 1 ? "factures fournisseurs" : "facture fournisseur";
    const ttcTxt = Math.round(ttc).toLocaleString("fr-FR");
    this.summaryTarget.textContent =
      `${total} ${factures} · ${pending} à valider · ${ttcTxt} € TTC sur les 12 derniers mois`;
  }

  _renderError() {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();
    this.tableBodyTarget.appendChild(this.cloneTemplate("tpl-purchase-error"));
    if (this.hasPaginationTarget) this.paginationTarget.hidden = true;
    if (this.hasPageInfoTarget) this.pageInfoTarget.textContent = "Erreur de chargement";
    if (this.hasSummaryTarget) this.summaryTarget.textContent = "—";
  }

  _clearTableBody() {
    while (this.tableBodyTarget.firstChild) {
      this.tableBodyTarget.removeChild(this.tableBodyTarget.firstChild);
    }
  }

  _updateSortIndicators() {
    const buttons = this.element.querySelectorAll("button[data-sort-key]");
    buttons.forEach((btn) => {
      const key = btn.dataset.sortKey;
      let indicator = "";
      if (this.sortValue === key) indicator = " ↑";
      else if (this.sortValue === `-${key}`) indicator = " ↓";
      const span = btn.querySelector(".nm-table__sort-indicator") ?? btn.querySelector("span:last-child");
      if (span && span.getAttribute("aria-hidden") === "true") {
        span.textContent = indicator;
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Export CSV
  // ---------------------------------------------------------------------------

  exportCsv() {
    if (!Array.isArray(this._lastResults) || this._lastResults.length === 0) {
      this.showError("Aucune facture à exporter.");
      return;
    }
    const header = [
      "Date", "Fournisseur", "SIRET", "HT", "TVA", "TTC", "Catégorie", "Statut",
    ];
    const rows = this._lastResults.map((inv) => [
      inv.issuedAt ?? "",
      inv.supplierName ?? "",
      inv.supplierSiret ?? "",
      this._csvAmount(inv.amountExclVat),
      this._csvAmount(inv.vatTotal),
      this._csvAmount(inv.amountInclVat),
      inv.expenseCategory ?? "",
      this._resolvePillKind(inv),
    ]);
    const escape = (cell) => {
      const s = String(cell ?? "");
      if (/[";\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const csv = [header, ...rows]
      .map((r) => r.map(escape).join(";"))
      .join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const today = new Date().toISOString().slice(0, 10);
    a.download = `factures-recues-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  _csvAmount(raw) {
    if (raw === null || raw === undefined || raw === "") return "";
    const num = Number(raw);
    if (!Number.isFinite(num)) return String(raw);
    return num.toFixed(2).replace(".", ",");
  }

  _formatAmount(raw, withSymbol = false) {
    if (raw === null || raw === undefined || raw === "") return "—";
    const num = Number(raw);
    if (!Number.isFinite(num)) return String(raw);
    const txt = num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return withSymbol ? `${txt} €` : txt;
  }

  // ---------------------------------------------------------------------------
  // Upload modal — open / drag&drop / submit
  // ---------------------------------------------------------------------------

  openUpload(event) {
    if (event) event.preventDefault();
    if (!this.hasUploadModalTarget) return;
    this._resetUploadState();
    if (typeof this.uploadModalTarget.showModal === "function") {
      this.uploadModalTarget.showModal();
    } else {
      this.uploadModalTarget.setAttribute("open", "");
    }
  }

  cancelUpload(event) {
    if (event && event.type === "cancel") {
      this._resetUploadState();
    }
  }

  cancelUploadButton() {
    this._resetUploadState();
    if (this.hasUploadModalTarget && typeof this.uploadModalTarget.close === "function") {
      this.uploadModalTarget.close();
    }
  }

  _resetUploadState() {
    this._pendingFile = null;
    this._uploading = false;
    if (this.hasFileInputTarget) this.fileInputTarget.value = "";
    if (this.hasUploadProgressTarget) this.uploadProgressTarget.hidden = true;
    if (this.hasUploadErrorTarget) {
      this.uploadErrorTarget.hidden = true;
      this.uploadErrorTarget.textContent = "Le fichier n'a pas pu être lu. Vérifiez qu'il s'agit bien d'un PDF de moins de 10 Mo.";
    }
    if (this.hasUploadSubmitTarget) this.uploadSubmitTarget.disabled = true;
    if (this.hasDropzoneTarget) this.dropzoneTarget.removeAttribute("data-dragover");
  }

  onDragOver(event) {
    event.preventDefault();
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.setAttribute("data-dragover", "true");
      this.dropzoneTarget.style.outline = "2px dashed var(--accent, currentColor)";
      this.dropzoneTarget.style.outlineOffset = "4px";
    }
  }

  onDragLeave(event) {
    event.preventDefault();
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.removeAttribute("data-dragover");
      this.dropzoneTarget.style.outline = "";
      this.dropzoneTarget.style.outlineOffset = "";
    }
  }

  onDrop(event) {
    event.preventDefault();
    this.onDragLeave(event);
    const file = event.dataTransfer?.files?.[0];
    if (file) this._acceptFile(file);
  }

  onFileSelected(event) {
    const file = event.target.files?.[0];
    if (file) this._acceptFile(file);
  }

  _acceptFile(file) {
    if (file.type && file.type !== "application/pdf") {
      this._showUploadError("Le fichier doit être au format PDF.");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      this._showUploadError("Le fichier dépasse la taille maximale de 10 Mo.");
      return;
    }
    this._pendingFile = file;
    if (this.hasUploadErrorTarget) this.uploadErrorTarget.hidden = true;
    if (this.hasUploadFileNameTarget) this.uploadFileNameTarget.textContent = file.name;
    if (this.hasUploadSubmitTarget) this.uploadSubmitTarget.disabled = false;
  }

  _showUploadError(message) {
    if (this.hasUploadErrorTarget) {
      this.uploadErrorTarget.hidden = false;
      this.uploadErrorTarget.textContent = message;
    }
    if (this.hasUploadSubmitTarget) this.uploadSubmitTarget.disabled = true;
    this._pendingFile = null;
  }

  async submitUpload(event) {
    if (event) event.preventDefault();
    if (!this._pendingFile || this._uploading) return;
    this._uploading = true;
    if (this.hasUploadProgressTarget) this.uploadProgressTarget.hidden = false;
    if (this.hasUploadErrorTarget) this.uploadErrorTarget.hidden = true;
    if (this.hasUploadSubmitTarget) this.uploadSubmitTarget.disabled = true;

    const idempotencyKey = (crypto.randomUUID && crypto.randomUUID())
      || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    try {
      const response = await api.uploadPurchaseInvoice(this._pendingFile, idempotencyKey);
      const id = response?.purchaseInvoice?.id ?? response?.id ?? response?.data?.id;
      if (!id) {
        this._showUploadError("Réponse inattendue de la passerelle. Réessayez.");
        this._uploading = false;
        if (this.hasUploadProgressTarget) this.uploadProgressTarget.hidden = true;
        return;
      }
      if (this.hasUploadModalTarget && typeof this.uploadModalTarget.close === "function") {
        this.uploadModalTarget.close();
      }
      window.location.assign(`/factures-recues-detail.html?id=${encodeURIComponent(id)}`);
    } catch (error) {
      this._uploading = false;
      if (this.hasUploadProgressTarget) this.uploadProgressTarget.hidden = true;
      if (this.hasUploadSubmitTarget) this.uploadSubmitTarget.disabled = false;
      const status = error instanceof APIError ? error.status : 0;
      if (status === 503) {
        if (this.hasUploadModalTarget && typeof this.uploadModalTarget.close === "function") {
          this.uploadModalTarget.close();
        }
        this.showError("La passerelle de facturation électronique est temporairement indisponible. Réessayez dans quelques instants.");
        return;
      }
      const msg = error instanceof APIError && error.message
        ? error.message
        : "L'analyse du PDF a échoué. Vérifiez le fichier puis réessayez.";
      this._showUploadError(msg);
    }
  }
}
